import { useEffect, useRef, useCallback, useState } from 'react';
import { useEventStore } from '@/stores/eventStore';
import { useTastingStore } from '@/stores/tastingStore';
import { TastingStep } from '@/types';

// WebSocket event types matching the backend
export type TastingEventType =
  | 'event.started'
  | 'event.completed'
  | 'event.cancelled'
  | 'step.locked'
  | 'step.unlocked'
  | 'step.advanced'
  | 'guest.joined'
  | 'guest.left'
  | 'guest.progress'
  | 'guest.completed'
  | 'moderator.message'
  | 'moderator.cleared'
  | 'sync';

export interface TastingEvent {
  type: TastingEventType;
  event_id: number;
  timestamp: string;
  data?: unknown;
}

export interface StepLockData {
  step: TastingStep;
  message?: string;
}

export interface GuestData {
  guest_id: number;
  guest_name?: string;
}

export interface GuestProgressData {
  guest_id: number;
  guest_name?: string;
  current_step: TastingStep;
  spirit_index: number;
}

export interface SyncData {
  event_status: string;
  current_step: TastingStep;
  is_locked: boolean;
  moderator_message?: string;
  guests?: Array<{
    guest_id: number;
    guest_name?: string;
    status: string;
    current_step?: TastingStep;
    is_anonymous: boolean;
    joined_at?: string;
    completed_at?: string;
  }>;
}

interface UseWebSocketOptions {
  accessCode: string;
  guestId?: number;
  guestName?: string;
  clientType?: 'host' | 'guest';
  onEvent?: (event: TastingEvent) => void;
}

export function useWebSocket(options: UseWebSocketOptions) {
  const { accessCode, guestId = 0, guestName = '', clientType = 'guest', onEvent } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const reconnectAttemptsRef = useRef(0);

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { setEventStarted, setModeratorMessage, setStepLocked } = useEventStore();
  const { setCurrentStep } = useTastingStore();

  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Build WebSocket URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = import.meta.env.VITE_WS_HOST || window.location.host;
    const params = new URLSearchParams({
      guestId: guestId.toString(),
      guestName,
      type: clientType,
    });
    const url = `${protocol}//${host}/ws/events/${accessCode}?${params}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setError(null);
      reconnectAttemptsRef.current = 0;
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const data: TastingEvent = JSON.parse(event.data);
        handleEvent(data);
        onEvent?.(data);
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    ws.onerror = (event) => {
      console.error('WebSocket error:', event);
      setError('Verbindungsfehler');
    };

    ws.onclose = () => {
      setIsConnected(false);
      wsRef.current = null;
      console.log('WebSocket disconnected');

      // Attempt reconnection
      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++;
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log(`Reconnecting... (attempt ${reconnectAttemptsRef.current})`);
          connect();
        }, reconnectDelay);
      } else {
        setError('Verbindung verloren. Bitte Seite neu laden.');
      }
    };
  }, [accessCode, guestId, guestName, clientType, onEvent]);

  const handleEvent = useCallback((event: TastingEvent) => {
    console.log('WebSocket event:', event.type, event.data);

    switch (event.type) {
      case 'event.started':
        setEventStarted(true);
        break;

      case 'event.completed':
      case 'event.cancelled':
        // Event is over, redirect or show message
        break;

      case 'step.locked': {
        const lockData = event.data as StepLockData;
        setStepLocked(true, lockData.step);
        if (lockData.message) {
          setModeratorMessage(lockData.message);
        }
        break;
      }

      case 'step.unlocked':
        setStepLocked(false, null);
        break;

      case 'step.advanced': {
        const advanceData = event.data as { next_step: TastingStep };
        setCurrentStep(advanceData.next_step);
        break;
      }

      case 'moderator.message': {
        const msgData = event.data as { message: string };
        setModeratorMessage(msgData.message);
        break;
      }

      case 'moderator.cleared':
        setModeratorMessage(null);
        break;

      case 'sync': {
        const syncData = event.data as SyncData;
        if (syncData.is_locked) {
          setStepLocked(true, syncData.current_step);
        }
        if (syncData.moderator_message) {
          setModeratorMessage(syncData.moderator_message);
        }
        break;
      }

      // Guest events are mainly for the host dashboard
      case 'guest.joined':
      case 'guest.left':
      case 'guest.progress':
      case 'guest.completed':
        // These are handled by the onEvent callback for the host
        break;
    }
  }, [setEventStarted, setModeratorMessage, setStepLocked, setCurrentStep]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendProgress = useCallback((currentStep: TastingStep, spiritIndex: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'guest.progress',
        data: {
          current_step: currentStep,
          spirit_index: spiritIndex,
        },
      }));
    }
  }, []);

  // Connect on mount
  useEffect(() => {
    if (accessCode) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [accessCode, connect, disconnect]);

  return {
    isConnected,
    error,
    connect,
    disconnect,
    sendProgress,
  };
}
